<template>
    <AdminLayout :crumbs="crumbs" :season_id="season_id" wide>
        <template v-if="stage && season">
            <!-- Header: what stage, its state and how to drive it. Sticky, so
                 the controls stay at hand however long the rundown is. -->
            <div ref="sentinel" class="rd-sentinel"></div>
            <div class="rd-head" :class="{ 'is-stuck': stuck, 'is-live': live_here }">
                <div class="rd-head-row">
                    <div class="min-w-0">
                        <div class="a-eyebrow rd-head-eyebrow">Etapa {{ stage_position }} de {{ season_stage_count }} · {{ season.name }}</div>
                        <h1 class="a-page-title rd-head-title">{{ stage.name }}</h1>
                        <div class="rd-meta">
                            <span class="a-pill" :class="status.cls">{{ status.label }}</span>
                            <span class="a-muted"><i class="bi bi-calendar3 me-1"></i>{{ range }}</span>
                            <span class="a-muted"><i class="bi bi-list-ol me-1"></i>{{ plural(activities.length, 'actividad', 'actividades') }}</span>
                            <span class="a-muted" v-if="stops.length"><i class="bi bi-signpost-split me-1"></i>{{ plural(stops.length, 'parada') }}</span>
                            <span class="a-muted" v-if="total_minutes"><i class="bi bi-clock me-1"></i>≈ {{ total_minutes }} min de juego</span>
                        </div>
                    </div>
                    <div class="a-page-actions" v-if="is_admin">
                        <div class="dropdown">
                            <button type="button" class="btn btn-secondary" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-plus-lg"></i>Añadir</button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="open_add_activity"><i class="bi bi-question-square"></i>Actividad</a></li>
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="open_add_sector"><i class="bi bi-signpost-split"></i>Parada</a></li>
                            </ul>
                        </div>
                        <div class="dropdown">
                            <button type="button" class="btn btn-secondary btn-icon" title="Más acciones" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-three-dots"></i></button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="$refs.qr.open(season)"><i class="bi bi-qr-code"></i>Acceso de los equipos</a></li>
                                <li><hr class="dropdown-divider"></li>
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="import_modal.show()"><i class="bi bi-upload"></i>Importar actividades (JSON)</a></li>
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="export_json"><i class="bi bi-braces"></i>Copiar como JSON</a></li>
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="export_csv"><i class="bi bi-filetype-csv"></i>Descargar CSV</a></li>
                                <li v-if="!is_playing && (stage.ended_at || has_answers)"><hr class="dropdown-divider"></li>
                                <li v-if="!is_playing && (stage.ended_at || has_answers)"><a class="dropdown-item text-danger" href="javascript:void(0)" @click="ask_restart"><i class="bi bi-arrow-counterclockwise"></i>Reiniciar etapa…</a></li>
                            </ul>
                        </div>
                        <template v-if="!live_here">
                            <button v-if="playback" type="button" class="btn btn-secondary" disabled title="Termina la etapa en directo antes de iniciar otra">Hay otra etapa en directo</button>
                            <button v-else-if="stage.ended_at" type="button" class="btn btn-primary" @click="ask_restart"><i class="bi bi-arrow-counterclockwise"></i>Volver a jugar</button>
                            <button v-else type="button" class="btn btn-accent" :disabled="!activities.length" @click="send('START_STAGE_PLAYBACK', stage.id)"><i class="bi bi-play-fill"></i>Iniciar etapa</button>
                        </template>
                    </div>
                </div>

                <!-- Live strip: only while this stage is what the room is playing -->
                <div v-if="live_here" class="rd-live">
                    <span class="rd-live-badge" :class="{ 'is-paused': live_paused }"><span class="rd-live-dot"></span>{{ live_paused ? 'En pausa' : 'En directo' }}</span>
                    <div class="rd-live-what min-w-0">
                        <span v-if="playback" class="rd-live-progress">Actividad {{ Math.min(playback.index + 1, playback.question_ids.length) }} de {{ playback.question_ids.length }}</span>
                        <span class="rd-live-title">{{ live_title }}</span>
                    </div>
                    <PlaybackControls variant="light" class="ms-auto flex-shrink-0" />
                </div>
            </div>

            <!-- Rundown -->
            <div class="a-card rd">
                <div class="a-card-header" v-if="items.length > 8">
                    <div class="a-search flex-fill">
                        <i class="bi bi-search"></i>
                        <input type="search" class="form-control" v-model="search" placeholder="Buscar en la etapa">
                    </div>
                </div>

                <!-- Fixed first stop ("salida"): always exists, can't move -->
                <div v-if="default_sector" class="rd-stop rd-stop-start" :class="{ 'is-live': is_sector_active(default_sector) }">
                    <i class="bi rd-stop-icon" :class="theme.icons.start"></i>
                    <div class="a-row-main">
                        <div class="rd-stop-name">{{ default_sector.name }}</div>
                        <div class="rd-stop-what">{{ (default_sector.resources || []).length ? sector_summary(default_sector) : 'Salida de la etapa · no muestra nada antes de la primera actividad' }}</div>
                        <div v-if="is_sector_active(default_sector)" class="rd-stop-live">{{ sector_live_label(default_sector) }}</div>
                    </div>
                    <div class="a-row-actions" v-if="is_admin">
                        <button v-if="is_sector_held(default_sector)" type="button" class="btn btn-primary btn-sm" @click="send('RESUME_STAGE_PLAYBACK', null)"><i class="bi bi-play-fill"></i>Reanudar</button>
                        <button type="button" class="btn btn-ghost btn-icon" title="Editar" @click="open_edit_sector(default_sector)"><i class="bi bi-pencil"></i></button>
                    </div>
                </div>

                <draggable v-if="filtered_items.length" v-model="filtered_items" tag="div" :item-key="(it) => it.kind + '-' + it.question.id"
                    handle=".a-handle" ghostClass="a-ghost" :disabled="!is_admin || !!search" @end="persist_order">
                    <template #item="{ element: it }">
                        <div v-if="it.kind == 'sector'" class="rd-stop" :class="{ 'is-live': is_sector_active(it.question) }">
                            <i v-if="is_admin" class="bi bi-grip-vertical a-handle" title="Arrastrar para reordenar"></i>
                            <i class="bi rd-stop-icon" :class="theme.icons.stop"></i>
                            <div class="a-row-main">
                                <div class="rd-stop-name">{{ it.question.name }}</div>
                                <div class="rd-stop-what">{{ sector_summary(it.question) }}</div>
                                <div v-if="is_sector_active(it.question)" class="rd-stop-live">{{ sector_live_label(it.question) }}</div>
                            </div>
                            <div class="a-row-actions" v-if="is_admin">
                                <button v-if="is_sector_held(it.question)" type="button" class="btn btn-primary btn-sm" @click="send('RESUME_STAGE_PLAYBACK', null)"><i class="bi bi-play-fill"></i>Reanudar</button>
                                <button v-else-if="!playback" type="button" class="btn btn-ghost btn-sm" title="Muestra ahora lo que tenga configurado (o solo la parada) y espera a Reanudar" @click="send('TEST_RUN_SECTOR_ACTION', it.question.id)"><i class="bi bi-play-circle"></i>Forzar parada</button>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-ghost btn-icon" title="Más acciones" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-three-dots"></i></button>
                                    <ul class="dropdown-menu dropdown-menu-end">
                                        <li><a class="dropdown-item" href="javascript:void(0)" @click="open_edit_sector(it.question)"><i class="bi bi-pencil"></i>Editar</a></li>
                                        <li><a class="dropdown-item text-danger" href="javascript:void(0)" @click="ask_delete_sector(it.question)"><i class="bi bi-trash3"></i>Eliminar</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div v-else class="rd-item" :class="{ 'is-live': question_is_live(it.question.id), 'is-open': open_id == it.question.id }" :id="'activity-' + it.question.id">
                            <div class="rd-item-row" @click="toggle(it.question.id)">
                                <i v-if="is_admin" class="bi bi-grip-vertical a-handle" @click.stop title="Arrastrar para reordenar"></i>
                                <div class="rd-num">{{ position_of(it.question.id) }}</div>
                                <span class="a-tag rd-type"><i class="bi" :class="type_icon(it.question.type)"></i>{{ type_label(it.question.type) }}</span>
                                <div class="a-row-main">
                                    <div class="a-row-title">{{ it.question.title || 'Sin título' }}</div>
                                </div>
                                <div class="rd-facts">
                                    <span v-if="it.question.duration"><i class="bi bi-stopwatch"></i>{{ it.question.duration }} s</span>
                                    <span v-if="!is_content(it.question)"><i class="bi bi-star"></i>{{ it.question.score || 0 }} pts</span>
                                    <span v-if="answered_count(it.question.id)"><i class="bi bi-people"></i>{{ answered_count(it.question.id) }}</span>
                                </div>
                                <span v-if="question_is_live(it.question.id)" class="a-pill a-pill-live">En directo</span>
                                <span v-else-if="it.question.start_time" class="a-pill a-pill-muted">Hecha</span>
                                <div class="dropdown" v-if="is_admin" @click.stop>
                                    <button type="button" class="btn btn-ghost btn-icon" title="Más acciones" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-three-dots"></i></button>
                                    <ul class="dropdown-menu dropdown-menu-end">
                                        <li><a class="dropdown-item" href="javascript:void(0)" @click="open_edit_activity(it.question)"><i class="bi bi-pencil"></i>Editar</a></li>
                                        <li><a class="dropdown-item" href="javascript:void(0)" @click="send('DUPLICATE_QUESTION', it.question.id)"><i class="bi bi-files"></i>Duplicar</a></li>
                                        <template v-if="!playback">
                                            <li><hr class="dropdown-divider"></li>
                                            <li><h6 class="dropdown-header">Lanzar a mano</h6></li>
                                            <li><a class="dropdown-item" href="javascript:void(0)" @click="send('LAUNCH_TITLE', it.question.id)"><i class="bi bi-card-heading"></i>Mostrar título</a></li>
                                            <li><a class="dropdown-item" href="javascript:void(0)" @click="open_launch(it.question)"><i class="bi bi-play-fill"></i>Lanzar con cuenta atrás…</a></li>
                                            <li v-if="question_is_live(it.question.id)"><a class="dropdown-item" href="javascript:void(0)" @click="send('END_ACTIVITY', it.question.id)"><i class="bi bi-flag"></i>Finalizar ahora</a></li>
                                        </template>
                                        <li><hr class="dropdown-divider"></li>
                                        <li><a class="dropdown-item text-danger" href="javascript:void(0)" @click="ask_delete_activity(it.question)"><i class="bi bi-trash3"></i>Eliminar</a></li>
                                    </ul>
                                </div>
                                <i class="bi rd-chevron" :class="open_id == it.question.id ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                            </div>

                            <!-- Detail: the question and how people answered -->
                            <div v-if="open_id == it.question.id" class="rd-detail">
                                <div class="rd-detail-facts">
                                    <span v-if="it.question.type == utils.ACTIVITY_TYPE.VIDEO && it.question.display_on == utils.MEDIA_DISPLAY.SCREEN"><i class="bi bi-display me-1"></i>Se ve en la pantalla grande</span>
                                    <span v-if="it.question.multiplier && it.question.multiplier != 1">Multiplicador ×{{ it.question.multiplier }}</span>
                                    <span v-if="it.question.segment">Tramo: {{ utils.get_segment_label(it.question.segment) }}</span>
                                    <span v-if="it.question.start_time">Jugada {{ utils.timestamp_to_hms(it.question.start_time) }}<template v-if="it.question.end_time"> → {{ utils.timestamp_to_hms(it.question.end_time) }}</template></span>
                                    <span v-if="media_waiting(it.question.id)" class="text-danger">Cargando vídeo: {{ $store.state.game.media_sync.ready.length }}/{{ $store.state.game.media_sync.total }} dispositivos</span>
                                </div>

                                <!-- Choice questions: distribution -->
                                <div v-if="has_choices(it.question)" class="rd-choices">
                                    <div v-for="(choice, idx) in it.question.choices" :key="idx" class="rd-choice" :class="{ 'is-correct': (it.question.correctAnswer || []).includes(idx) }">
                                        <div class="rd-choice-bar" :style="{ width: choice_share(it.question.id, idx) + '%' }"></div>
                                        <span class="rd-choice-letter">{{ utils.get_choice_character(idx) }}</span>
                                        <span class="rd-choice-text">{{ choice }}</span>
                                        <i v-if="(it.question.correctAnswer || []).includes(idx)" class="bi bi-check-circle-fill rd-choice-ok" title="Correcta"></i>
                                        <span class="rd-choice-count" :title="names_for_choice(it.question.id, idx)">{{ users_for_choice(it.question.id, idx).length }}</span>
                                    </div>
                                </div>

                                <!-- Free answer: the right one and what people typed -->
                                <div v-else-if="it.question.type == utils.ACTIVITY_TYPE.TEXT || it.question.type == utils.ACTIVITY_TYPE.RANGE">
                                    <div class="mb-2"><span class="a-muted">Respuesta correcta:</span> <b>{{ (it.question.correctAnswer || [])[0] }}</b></div>
                                    <div class="rd-answers" v-if="clients_with_answer(it.question.id).length">
                                        <button v-for="entry in clients_with_answer(it.question.id)" :key="entry.user.id" type="button" class="rd-answer" @click="$refs.user.open(entry.user.uuid)">
                                            <b>{{ entry.user.username }}</b> {{ entry.answer.choice[0] }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Content -->
                                <div v-else-if="is_content(it.question)">
                                    <div v-if="it.question.media_url" class="text-break mb-2"><a :href="it.question.media_url" target="_blank" rel="noopener"><i class="bi bi-box-arrow-up-right me-1"></i>{{ it.question.media_url }}</a></div>
                                    <div class="a-muted">Visto por {{ answered_count(it.question.id) }} equipos.</div>
                                </div>

                                <div class="a-subtle mt-2" v-if="has_choices(it.question) && answered_count(it.question.id)">{{ answered_count(it.question.id) }} equipos han respondido. Pasa el ratón por un número para ver quién.</div>
                            </div>
                        </div>
                    </template>
                </draggable>

                <div v-else-if="search" class="a-empty">Nada en esta etapa coincide con "{{ search }}".</div>
                <div v-else class="a-empty">
                    <i class="bi bi-list-ol"></i>
                    <div class="a-empty-title">Etapa vacía</div>
                    <div>Añade actividades (preguntas o contenido) y, si quieres, paradas entre ellas para mostrar rankings o feedback.</div>
                    <div class="d-flex gap-2 justify-content-center mt-3" v-if="is_admin">
                        <button type="button" class="btn btn-primary" @click="open_add_activity"><i class="bi bi-plus-lg"></i>Añadir actividad</button>
                        <button type="button" class="btn btn-secondary" @click="import_modal.show()"><i class="bi bi-upload"></i>Importar JSON</button>
                    </div>
                </div>
            </div>
            <p class="a-subtle mt-2" v-if="is_admin && items.length > 1">Arrastra <i class="bi bi-grip-vertical"></i> para cambiar el orden. Las paradas se ejecutan al llegar a ellas durante la reproducción.</p>
        </template>
        <div v-else class="a-card a-empty">
            <i class="bi bi-question-circle"></i>
            <div class="a-empty-title">No se ha encontrado esta etapa</div>
            <router-link :to="{ name: 'Panel' }" class="btn btn-secondary mt-2">Volver al listado</router-link>
        </div>

        <!-- ===== Modals ===== -->

        <!-- Add activity: pick a type, then its form -->
        <div class="modal fade a-modal" ref="addModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="d-flex align-items-center gap-2">
                            <button v-if="type" type="button" class="btn btn-ghost btn-icon" title="Elegir otro tipo" @click="type = null"><i class="bi bi-arrow-left"></i></button>
                            <h1 class="modal-title">{{ type ? 'Nueva actividad · ' + type_label(type) : 'Nueva actividad' }}</h1>
                        </div>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <template v-if="!type">
                            <div class="a-eyebrow mb-2">Preguntas · puntúan</div>
                            <div class="type-grid mb-4">
                                <button v-for="t in question_types" :key="t.type" type="button" class="type-card" :disabled="t.disabled" @click="type = t.type">
                                    <i class="bi" :class="type_icon(t.type)"></i>
                                    <span class="type-card-name">{{ type_label(t.type) }}</span>
                                    <span class="type-card-desc">{{ t.desc }}</span>
                                </button>
                            </div>
                            <div class="a-eyebrow mb-2">Contenido · no puntúa, se completa al verlo</div>
                            <div class="type-grid">
                                <button v-for="t in content_types" :key="t.type" type="button" class="type-card" @click="type = t.type">
                                    <i class="bi" :class="type_icon(t.type)"></i>
                                    <span class="type-card-name">{{ type_label(t.type) }}</span>
                                    <span class="type-card-desc">{{ t.desc }}</span>
                                </button>
                            </div>
                        </template>
                        <template v-else-if="question">
                            <FormQuiz v-if="type == utils.ACTIVITY_TYPE.QUIZ" :form="question"></FormQuiz>
                            <FormQuizMultiple v-if="type == utils.ACTIVITY_TYPE.QUIZ_MULTIPLE" :form="question"></FormQuizMultiple>
                            <FormTrueFalse v-if="type == utils.ACTIVITY_TYPE.TRUE_FALSE" :form="question"></FormTrueFalse>
                            <FormRange v-if="type == utils.ACTIVITY_TYPE.RANGE" :form="question"></FormRange>
                            <FormCrossWord v-if="type == utils.ACTIVITY_TYPE.CROSSWORD" :form="question"></FormCrossWord>
                            <FormText v-if="type == utils.ACTIVITY_TYPE.TEXT" :form="question"></FormText>
                            <FormContent v-if="utils.is_content_activity(type)" :form="question" :type="type"></FormContent>
                        </template>
                    </div>
                    <div class="modal-footer" v-if="type && question">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="save_activity">Crear actividad</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit activity -->
        <div class="modal fade a-modal" ref="editModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title">Editar actividad<span v-if="editing" class="a-muted fw-normal"> · {{ type_label(editing.type) }}</span></h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body" v-if="editing">
                        <FormQuiz v-if="editing.type == utils.ACTIVITY_TYPE.QUIZ" :form="editing"></FormQuiz>
                        <FormQuizMultiple v-if="editing.type == utils.ACTIVITY_TYPE.QUIZ_MULTIPLE" :form="editing"></FormQuizMultiple>
                        <FormCrossWord v-if="editing.type == utils.ACTIVITY_TYPE.CROSSWORD" :form="editing"></FormCrossWord>
                        <FormRange v-if="editing.type == utils.ACTIVITY_TYPE.RANGE" :form="editing"></FormRange>
                        <FormTrueFalse v-if="editing.type == utils.ACTIVITY_TYPE.TRUE_FALSE" :form="editing"></FormTrueFalse>
                        <FormText v-if="editing.type == utils.ACTIVITY_TYPE.TEXT" :form="editing"></FormText>
                        <FormContent v-if="utils.is_content_activity(editing.type)" :form="editing"></FormContent>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="save_edit">Guardar cambios</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Stop (sector) -->
        <div class="modal fade a-modal" ref="sectorModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content" v-if="sector">
                    <div class="modal-header">
                        <h1 class="modal-title">{{ sector.id !== undefined ? 'Editar parada' : 'Nueva parada' }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-4">
                            <label class="form-label" for="sector-name">Nombre</label>
                            <input id="sector-name" type="text" class="form-control" v-model="sector.name" placeholder="Ej. Alto de la Bonaigua" @keyup.enter="save_sector">
                            <div class="form-text" v-if="sector.id === undefined">Se añade al final de la etapa; luego puedes arrastrarla a donde toque.</div>
                        </div>

                        <label class="form-label">Qué se muestra al llegar, en este orden</label>
                        <div class="form-text mt-0 mb-2">Si no añades nada, es una parada sin más: el juego se detiene hasta que pulses Reanudar.</div>
                        <draggable tag="div" v-model="sector_resources" item-key="_key" handle=".a-handle" ghostClass="a-ghost">
                            <template #item="{ element: resource, index }">
                                <div class="res-row">
                                    <i class="bi bi-grip-vertical a-handle"></i>
                                    <div class="flex-fill">
                                        <template v-if="resource.type === 'ranking'">
                                            <div class="res-title"><i class="bi bi-bar-chart-line"></i>Ranking</div>
                                            <select class="form-select form-select-sm" v-model="resource.ranking_id">
                                                <option :value="null">Elige un ranking…</option>
                                                <option v-for="rk in season_rankings" :key="rk.id" :value="rk.id">{{ rk.name }} · {{ utils.get_ranking_criterion_label(rk.criterion) }}, {{ utils.get_ranking_scope_label(rk.scope).toLowerCase() }}</option>
                                            </select>
                                            <div class="form-text" v-if="!season_rankings.length">Este {{ season_label.toLowerCase() }} no tiene rankings: créalos en la pestaña Rankings.</div>
                                        </template>
                                        <template v-else>
                                            <div class="res-title"><i class="bi bi-chat-square-text"></i>Feedback de actividades</div>
                                            <select class="form-select form-select-sm" multiple v-model="resource.question_ids" :size="Math.min(6, Math.max(3, feedback_candidates.length))">
                                                <option v-for="q in feedback_candidates" :key="q.id" :value="q.id">{{ position_of(q.id) }}. {{ q.question.title }}</option>
                                            </select>
                                            <div class="form-text">Ctrl/Cmd + clic para elegir varias. Se muestran una tras otra.</div>
                                        </template>
                                    </div>
                                    <button type="button" class="btn btn-ghost btn-icon" title="Quitar" @click="sector.resources.splice(index, 1)"><i class="bi bi-x-lg"></i></button>
                                </div>
                            </template>
                        </draggable>
                        <div class="d-flex gap-2 mt-2">
                            <button type="button" class="btn btn-secondary btn-sm" @click="add_sector_resource('ranking')"><i class="bi bi-plus"></i>Ranking</button>
                            <button type="button" class="btn btn-secondary btn-sm" @click="add_sector_resource('feedback')" :disabled="!feedback_candidates.length"><i class="bi bi-plus"></i>Feedback</button>
                        </div>

                        <div class="form-check mt-4" v-if="mode == utils.APP_MODES.ONLINE">
                            <input class="form-check-input" type="checkbox" id="sectorNotifyEmail" v-model="sector.notify_email">
                            <label class="form-check-label" for="sectorNotifyEmail">Avisar por email a los equipos al llegar</label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="save_sector">{{ sector.id !== undefined ? 'Guardar' : 'Crear parada' }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Manual launch with synchronised countdown -->
        <div class="modal fade a-modal" ref="launchModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content" v-if="launching">
                    <div class="modal-header">
                        <h1 class="modal-title">Lanzar «{{ launching.title }}»</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <label class="form-label" for="launch-seconds">Cuenta atrás antes de empezar</label>
                        <div class="input-group mb-3" style="max-width: 200px">
                            <input id="launch-seconds" type="number" min="5" max="20" class="form-control" v-model.number="seconds">
                            <span class="input-group-text">segundos</span>
                        </div>
                        <div class="a-eyebrow mb-2">Equipos sincronizados {{ synced_count }}/{{ players.length }}</div>
                        <div class="a-card">
                            <ul class="a-list" v-if="players.length">
                                <li class="a-row py-2" v-for="p in players" :key="p.uuid">
                                    <span class="a-row-main">{{ p.username }}</span>
                                    <span class="a-pill" :class="is_synced(p.id) ? 'a-pill-ok' : 'a-pill-muted'">{{ is_synced(p.id) ? 'Listo' : 'Esperando' }}</span>
                                </li>
                            </ul>
                            <div v-else class="a-empty py-4">No hay equipos conectados.</div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
                        <button v-if="$store.state.game.synchronizing == false" type="button" class="btn btn-accent" @click="launch_activity"><i class="bi bi-play-fill"></i>Comenzar</button>
                        <button v-else type="button" class="btn btn-danger" @click="send('CANCEL_LAUNCH_ACTIVITY', null)">Cancelar lanzamiento</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Import JSON -->
        <div class="modal fade a-modal" ref="importModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title">Importar actividades</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <p class="a-muted">Las actividades del archivo se añaden al final de <b>{{ stage && stage.name }}</b>. No se borra nada.</p>
                        <input ref="importFile" type="file" accept=".json,application/json" class="form-control">
                        <div class="form-text">Formato: <code>{ "questions": [ { "title": ..., "type": ..., ... } ] }</code>, el mismo que genera "Copiar como JSON".</div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="import_json">Importar</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirmations -->
        <div class="modal fade a-modal" ref="confirmModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="confirm">
                    <div class="modal-header">
                        <h1 class="modal-title">{{ confirm.title }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">{{ confirm.text }}</div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="confirm.run(); confirm_modal.hide()">{{ confirm.action }}</button>
                    </div>
                </div>
            </div>
        </div>

        <UserDetailModal ref="user" />
        <EventQrModal ref="qr" />
    </AdminLayout>
</template>

<script>
import { Modal } from 'bootstrap'
import draggable from 'vuedraggable'
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import EventQrModal from '@/components/admin/EventQrModal.vue'
import UserDetailModal from '@/components/admin/UserDetailModal.vue'
import PlaybackControls from '@/components/admin/PlaybackControls.vue'
import FormQuiz from '@/components/form/FormQuiz.vue'
import FormQuizMultiple from '@/components/form/FormQuizMultiple.vue'
import FormCrossWord from '@/components/form/FormCrossWord.vue'
import FormRange from '@/components/form/FormRange.vue'
import FormTrueFalse from '@/components/form/FormTrueFalse.vue'
import FormText from '@/components/form/FormText.vue'
import FormContent from '@/components/form/FormContent.vue'
import { stage_status, format_range, activity_type_label, activity_type_icon, plural } from '@/utils/admin-format'
import { get_theme } from '@/themes'

// One stage as a rundown: its activities and stops in play order, what's
// live, and how each activity went. Everything about building a stage
// (add/edit/reorder/import) and running parts of it by hand lives here;
// running the whole stage is the header's "Iniciar etapa" + the LiveDock.
export default {
    name: 'StageRundown',
    components: {
        AdminLayout, EventQrModal, UserDetailModal, PlaybackControls, draggable,
        FormQuiz, FormQuizMultiple, FormCrossWord, FormRange, FormTrueFalse, FormText, FormContent,
    },
    data(){
        return {
            utils,
            mode: window.MODE,
            search: '',
            filtered_items: [],
            open_id: null,
            // Add activity (type picker + Form*.vue). Each Form*.vue sends its
            // own REGISTER/EDIT_ACTIVITY from the validate() it hangs on
            // `question`/`editing` (see the forms' mounted()).
            type: null,
            question: null,
            editing: null,
            sector: null,
            launching: null,
            seconds: 10,
            confirm: null,
            // The sticky header has left its resting place (see mounted).
            stuck: false,
            question_types: [
                { type: utils.ACTIVITY_TYPE.QUIZ, desc: 'Una respuesta correcta entre varias.' },
                { type: utils.ACTIVITY_TYPE.QUIZ_MULTIPLE, desc: 'Varias respuestas correctas.' },
                { type: utils.ACTIVITY_TYPE.TRUE_FALSE, desc: 'Dos opciones, una verdadera.' },
                { type: utils.ACTIVITY_TYPE.RANGE, desc: 'Un número dentro de un rango.' },
                { type: utils.ACTIVITY_TYPE.TEXT, desc: 'Escribir una palabra.' },
                { type: utils.ACTIVITY_TYPE.CROSSWORD, desc: 'Próximamente.', disabled: true },
            ],
            content_types: [
                { type: utils.ACTIVITY_TYPE.IMAGE, desc: 'Una imagen con texto opcional.' },
                { type: utils.ACTIVITY_TYPE.VIDEO, desc: 'YouTube, Vimeo o un archivo.' },
                { type: utils.ACTIVITY_TYPE.PDF, desc: 'Un documento para leer.' },
                { type: utils.ACTIVITY_TYPE.HTML, desc: 'Contenido maquetado a medida.' },
            ],
        };
    },
    computed: {
        game(){ return this.$store.state.game; },
        season_id(){ return parseInt(this.$route.params.season_id, 10); },
        stage_id(){ return parseInt(this.$route.params.stage_id, 10); },
        season(){ return (this.game.seasons || []).find((s) => s.id == this.season_id) || null; },
        stage(){ return (this.game.stages || []).find((s) => s.id == this.stage_id) || null; },
        season_label(){ return utils.get_season_label(this.mode); },
        theme(){ return get_theme(this.season && this.season.theme); },
        crumbs(){
            return [
                { label: utils.get_season_label(this.mode, true), to: { name: 'Panel' } },
                { label: this.season ? this.season.name : '…', to: { name: 'Stages', params: { season_id: this.season_id } } },
                { label: this.stage ? this.stage.name : 'Etapa' },
            ];
        },
        is_admin(){ return !!this.game.user && this.game.user.role == utils.USER_ROLES.ADMINISTRATOR; },
        playback(){ return this.game.stage_playback; },
        is_playing(){ return !!(this.playback && this.playback.stage_id == this.stage_id); },
        // This stage is what the room has on screen: playing it, or one of
        // its activities/stops launched by hand.
        live_here(){
            if(this.is_playing) return true;
            if(this.playback) return false;
            const g = this.game;
            if(!g.state || [utils.GAME_STATES.IDLE, utils.GAME_STATES.AUTH].includes(g.state)) return false;
            if(g.state == utils.GAME_STATES.PAUSED)
                return (g.sectors || []).some((se) => se.id == g.paused_sector_id && se.stage_id == this.stage_id);
            return this.activities.some((q) => q.id == g.current_question);
        },
        live_paused(){
            return this.game.state == utils.GAME_STATES.PAUSED || !!(this.playback && !this.playback.active);
        },
        live_title(){
            const g = this.game;
            if(g.state == utils.GAME_STATES.PAUSED) return 'En la parada «' + (g.paused_sector_name || '') + '»';
            if(g.state == utils.GAME_STATES.INTERLUDE) return 'Animación de ' + (g.interlude && g.interlude.kind == 'intro' ? 'entrada' : 'llegada a parada');
            if(this.playback && this.playback.current_sector_id != null){
                const se = this.stops.find((x) => x.id == this.playback.current_sector_id) || this.default_sector;
                return se ? 'En la parada «' + se.name + '»' : 'En una parada';
            }
            const q = g.question && g.question.question;
            return q ? (this.position_of(q.id) + '. ' + q.title) : '';
        },
        status(){ return stage_status(this.stage, this.playback); },
        range(){ return format_range(this.stage.start_date, this.stage.end_date); },
        season_stage_count(){ return (this.game.stages || []).filter((s) => s.season_id == this.season_id).length; },
        stage_position(){
            const ordered = (this.game.stages || []).filter((s) => s.season_id == this.season_id).slice().sort((a, b) => a.order - b.order);
            return ordered.findIndex((s) => s.id == this.stage_id) + 1;
        },
        activities(){
            return (this.game.questions || []).filter((q) => q.question && q.question.stage_id == this.stage_id);
        },
        stops(){
            return (this.game.sectors || []).filter((se) => se.stage_id == this.stage_id && !se.is_default);
        },
        default_sector(){
            return (this.game.sectors || []).find((se) => se.stage_id == this.stage_id && se.is_default) || null;
        },
        // Activities + stops merged in play order (order shares one value
        // space, see reorder_activities in server.js); stable sort keeps
        // legacy rows without order in creation order.
        items(){
            const acts = this.activities.map((q) => ({ id: q.id, kind: 'activity', question: q.question }));
            const secs = this.stops.map((se) => ({ id: se.id, kind: 'sector', question: Object.assign({}, se, { type: utils.ACTIVITY_TYPE.SECTOR }) }));
            return acts.concat(secs).sort((a, b) => (a.question.order || 0) - (b.question.order || 0));
        },
        // Activity number as players see it (stops don't count).
        positions(){
            const map = new Map();
            this.items.filter((it) => it.kind == 'activity').forEach((it, i) => map.set(it.question.id, i + 1));
            return map;
        },
        total_minutes(){
            const seconds = this.activities.reduce((sum, q) => sum + (Number(q.question.duration) || 0) + (Number(q.question.title_duration) || 0), 0);
            return seconds ? Math.round(seconds / 60) : 0;
        },
        has_answers(){
            return this.activities.some((q) => this.answered_count(q.id) > 0);
        },
        season_rankings(){
            return (this.game.rankings || []).filter((r) => r.season_id == this.season_id);
        },
        feedback_candidates(){
            return this.activities.filter((q) => !utils.is_content_activity(q.question.type))
                .slice().sort((a, b) => this.position_of(a.id) - this.position_of(b.id));
        },
        sector_resources: {
            get(){ return (this.sector && this.sector.resources) || []; },
            set(value){ if(this.sector) this.sector.resources = value; },
        },
        players(){
            return (this.game.clients || []).filter((c) => c.role == utils.USER_ROLES.PLAYER);
        },
        synced_count(){
            return this.players.filter((p) => this.is_synced(p.id)).length;
        },
        // Map<question_id, { byChoice: Map<choice, user[]>, byUser: Map<user_id, answer> }>,
        // built once per change of clients/users instead of per render
        // (connected clients first, then the registered roster - online
        // players only ever appear in the latter).
        activity_index(){
            const index = new Map();
            const seen = new Set();
            const add = (user) => {
                if(!user.data || user.id == null || seen.has(user.id)) return;
                seen.add(user.id);
                user.data.forEach((act) => {
                    if(!index.has(act.question)) index.set(act.question, { byChoice: new Map(), byUser: new Map() });
                    const entry = index.get(act.question);
                    entry.byUser.set(user.id, { user, answer: act });
                    (act.choice || []).forEach((choice) => {
                        if(!entry.byChoice.has(choice)) entry.byChoice.set(choice, []);
                        entry.byChoice.get(choice).push(user);
                    });
                });
            };
            (this.game.clients || []).forEach(add);
            (this.game.users || []).forEach(add);
            return index;
        }
    },
    watch: {
        items: { immediate: true, handler(){ this.apply_filter(); } },
        search(){ this.apply_filter(); },
        // Follow what's live: expand it as playback reaches it.
        'game.current_question'(id){
            if(id && this.activities.some((q) => q.id == id)) this.open_id = id;
        },
    },
    mounted(){
        this.add_modal = new Modal(this.$refs.addModal);
        this.edit_modal = new Modal(this.$refs.editModal);
        this.sector_modal = new Modal(this.$refs.sectorModal);
        this.launch_modal = new Modal(this.$refs.launchModal);
        this.import_modal = new Modal(this.$refs.importModal);
        this.confirm_modal = new Modal(this.$refs.confirmModal);
        this.$refs.addModal.addEventListener('hidden.bs.modal', () => { this.question = null; this.type = null; });
        this.$refs.editModal.addEventListener('hidden.bs.modal', () => { this.editing = null; });
        this.$refs.launchModal.addEventListener('show.bs.modal', () => {
            this.$store.commit('setGame', Object.assign({}, this.game, { synchronizing: false }));
        });
        if(this.game.current_question && this.question_is_live(this.game.current_question))
            this.open_id = this.game.current_question;

        // Shadow + compact header once it's stuck to the top while scrolling.
        const root = this.$refs.sentinel && this.$refs.sentinel.closest('.a-content');
        if(root && window.IntersectionObserver){
            this.sticky_observer = new IntersectionObserver(([entry]) => { this.stuck = !entry.isIntersecting; }, { root, threshold: 0 });
            this.sticky_observer.observe(this.$refs.sentinel);
        }
    },
    beforeUnmount(){
        if(this.sticky_observer) this.sticky_observer.disconnect();
        ['add_modal', 'edit_modal', 'sector_modal', 'launch_modal', 'import_modal', 'confirm_modal'].forEach((m) => this[m] && this[m].dispose());
    },
    methods: {
        plural,
        type_label: activity_type_label,
        type_icon: activity_type_icon,
        send(rpc, params){
            window.network.wso.send(JSON.stringify({ action: window.RPC[rpc], params }));
        },
        // <draggable> needs a plain mutable array.
        apply_filter(){
            const term = this.search.trim().toLowerCase();
            this.filtered_items = this.items.filter((it) => ((it.question.title || it.question.name || '')).toLowerCase().includes(term));
        },
        persist_order(){
            this.send('REORDER_ACTIVITIES', { order: this.filtered_items.map((it) => ({ id: it.question.id, kind: it.kind })) });
        },
        position_of(id){ return this.positions.get(id) || '–'; },
        toggle(id){ this.open_id = this.open_id == id ? null : id; },
        is_content(q){ return utils.is_content_activity(q.type); },
        has_choices(q){
            return [utils.ACTIVITY_TYPE.QUIZ, utils.ACTIVITY_TYPE.QUIZ_MULTIPLE, utils.ACTIVITY_TYPE.TRUE_FALSE].includes(q.type) && Array.isArray(q.choices);
        },

        // --- Live state -------------------------------------------------
        // A stop takes over "what's live" while playback runs its content
        // or holds there; current_question can sit on the last activity
        // played the whole time.
        question_is_live(question_id){
            if(this.game.current_question != question_id) return false;
            if(this.playback && this.playback.current_sector_id != null) return false;
            return ![utils.GAME_STATES.PAUSED, utils.GAME_STATES.IDLE, utils.GAME_STATES.INTERLUDE].includes(this.game.state);
        },
        is_sector_running(sector){
            return !!(this.playback && this.playback.current_sector_id === sector.id);
        },
        is_sector_held(sector){
            return this.game.state == utils.GAME_STATES.PAUSED && this.game.paused_sector_id == sector.id;
        },
        is_sector_active(sector){
            return this.is_sector_running(sector) || this.is_sector_held(sector);
        },
        sector_live_label(sector){
            if(this.is_sector_held(sector)) return 'En la parada · esperando a que pulses Reanudar';
            const i = this.playback ? this.playback.current_resource_index : null;
            const res = i != null ? (sector.resources || [])[i] : null;
            if(!res) return 'Llegando a la parada';
            return 'Mostrando ' + (res.type == 'ranking' ? 'ranking' : 'feedback') + ' (' + (i + 1) + ' de ' + sector.resources.length + ')';
        },
        sector_summary(sector){
            const res = sector.resources || [];
            if(!res.length) return 'Solo parada: el juego espera a que pulses Reanudar';
            return res.map((r) => {
                if(r.type == 'ranking'){
                    const rk = this.season_rankings.find((x) => x.id == r.ranking_id);
                    return 'Ranking: ' + (rk ? rk.name : '¿eliminado?');
                }
                const n = (r.question_ids || []).length;
                return 'Feedback de ' + n + (n == 1 ? ' actividad' : ' actividades');
            }).join(' · ');
        },
        media_waiting(question_id){
            const sync = this.game.media_sync;
            return !!(sync && sync.question_id == question_id && !sync.play_at);
        },

        // --- Answers ----------------------------------------------------
        answered_count(question_id){
            const entry = this.activity_index.get(question_id);
            return entry ? entry.byUser.size : 0;
        },
        users_for_choice(question_id, idx){
            const entry = this.activity_index.get(question_id);
            return (entry && entry.byChoice.get(idx)) || [];
        },
        names_for_choice(question_id, idx){
            return this.users_for_choice(question_id, idx).map((u) => u.username).join(', ') || 'Nadie';
        },
        choice_share(question_id, idx){
            const total = this.answered_count(question_id);
            return total ? Math.round(this.users_for_choice(question_id, idx).length * 100 / total) : 0;
        },
        clients_with_answer(question_id){
            const entry = this.activity_index.get(question_id);
            return entry ? Array.from(entry.byUser.values()) : [];
        },
        is_synced(id){
            return (this.game.handshakes || []).some((u) => u.id == id && u.response == true);
        },

        // --- Activities -------------------------------------------------
        open_add_activity(){
            this.type = null;
            this.question = { stage_id: this.stage_id };
            this.add_modal.show();
        },
        open_edit_activity(q){
            const copy = Object.assign({}, q);
            if(typeof copy.choices === 'string') copy.choices = JSON.parse(copy.choices);
            this.editing = copy;
            this.edit_modal.show();
        },
        save_activity(){
            if(this.question.validate && this.question.validate() == true)
                this.add_modal.hide();
        },
        save_edit(){
            if(this.editing.validate && this.editing.validate() == true)
                this.edit_modal.hide();
        },
        ask_delete_activity(q){
            this.confirm = {
                title: 'Eliminar actividad',
                text: '«' + (q.title || 'Sin título') + '» y sus respuestas desaparecen de la etapa. No se puede deshacer.',
                action: 'Eliminar',
                run: () => this.send('REMOVE_ACTIVITY', q.id),
            };
            this.confirm_modal.show();
        },
        open_launch(q){
            this.launching = q;
            this.launch_modal.show();
        },
        launch_activity(){
            this.send('LAUNCH_ACTIVITY', { question_id: this.launching.id, timestamp: Date.now() + (this.seconds + 2) * 1000 });
        },

        // --- Stops ------------------------------------------------------
        open_add_sector(){
            this.sector = { name: '', type: utils.ACTIVITY_TYPE.SECTOR, stage_id: this.stage_id, resources: [] };
            this.sector_modal.show();
        },
        open_edit_sector(sector){
            this.sector = Object.assign({}, sector);
            this.sector.resources = (sector.resources || []).map((r) => Object.assign({ _key: 'r' + Math.random() }, r, r.question_ids ? { question_ids: r.question_ids.slice() } : {}));
            this.sector_modal.show();
        },
        add_sector_resource(type){
            const resource = type === 'ranking' ? { type: 'ranking', ranking_id: null } : { type: 'feedback', question_ids: [] };
            resource._key = 'r' + Date.now() + '_' + Math.floor(Math.random() * 100000);
            this.sector.resources.push(resource);
        },
        save_sector(){
            if(!this.sector.name){
                window.toast.warning('Ponle un nombre a la parada.');
                return;
            }
            const payload = Object.assign({}, this.sector);
            const before = (payload.resources || []).length;
            payload.resources = (payload.resources || [])
                .filter((r) => (r.type === 'ranking' && r.ranking_id) || (r.type === 'feedback' && r.question_ids && r.question_ids.length))
                .map((r) => { const clean = Object.assign({}, r); delete clean._key; return clean; });
            if(payload.resources.length != before)
                window.toast.warning('Se han quitado los elementos sin ranking o sin actividades elegidas.');
            this.send(this.sector.id !== undefined ? 'EDIT_SECTOR' : 'REGISTER_SECTOR', payload);
            this.sector_modal.hide();
        },
        ask_delete_sector(sector){
            this.confirm = {
                title: 'Eliminar parada',
                text: '«' + sector.name + '» deja de ejecutarse en la etapa. Las actividades no se tocan.',
                action: 'Eliminar parada',
                run: () => this.send('REMOVE_SECTOR', sector.id),
            };
            this.confirm_modal.show();
        },

        // --- Stage --------------------------------------------------------
        ask_restart(){
            this.confirm = {
                title: 'Reiniciar ' + this.stage.name,
                text: 'Se borran todas las respuestas dadas en esta etapa y se vuelve a lanzar desde el principio. No se puede deshacer.',
                action: 'Reiniciar etapa',
                run: () => this.send('RESTART_STAGE', this.stage_id),
            };
            this.confirm_modal.show();
        },

        // --- Import / export (this stage only) --------------------------
        export_rows(){
            return this.items.filter((it) => it.kind == 'activity').map((it) => {
                const q = it.question;
                const row = { title: q.title, type: q.type, score: q.score, duration: q.duration, choices: q.choices, correctAnswer: q.correctAnswer };
                if(q.type == utils.ACTIVITY_TYPE.TEXT) row.text = q.text;
                if(q.type == utils.ACTIVITY_TYPE.RANGE) Object.assign(row, { min: q.min, max: q.max, normal: q.normal });
                if(utils.is_content_activity(q.type)) Object.assign(row, { media_url: q.media_url, html: q.html, description: q.description });
                return row;
            });
        },
        export_json(){
            navigator.clipboard.writeText(JSON.stringify({ questions: this.export_rows() }, null, 4))
                .then(() => window.toast.success('Actividades copiadas al portapapeles'));
        },
        export_csv(){
            const rows = this.export_rows();
            const cols = ['title', 'type', 'score', 'duration', 'choices', 'correctAnswer'];
            const cell = (v) => '"' + String(Array.isArray(v) ? v.join(' | ') : (v == null ? '' : v)).replace(/"/g, '""') + '"';
            const csv = [cols.join(',')].concat(rows.map((r) => cols.map((c) => cell(r[c])).join(','))).join('\r\n');
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
            a.download = (this.stage.name || 'etapa').replace(/[^\w-]+/g, '_') + '.csv';
            a.click();
            URL.revokeObjectURL(a.href);
        },
        import_json(){
            const file = this.$refs.importFile.files[0];
            if(!file){
                window.toast.error('Elige un archivo .json');
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                this.send('UPLOAD_JSON', { stage_id: this.stage_id, content: e.target.result });
                this.$refs.importFile.value = '';
                this.import_modal.hide();
            };
            reader.readAsText(file);
        }
    }
}
</script>

<style lang="scss" scoped>
.min-w-0{ min-width: 0; }

/* --- Sticky header ---------------------------------------------------
   Bleeds into .a-content-inner's padding so it covers the full width
   while stuck; the 1px sentinel above it tells us when that happens. */
.rd-sentinel{ height: 1px; margin-top: -1px; }
.rd-head{
    position: sticky;
    top: 0;
    z-index: 6;
    margin: -28px -32px 20px;
    padding: 24px 32px 16px;
    background: var(--a-canvas);
    transition: box-shadow 0.2s, padding 0.2s;
}
.rd-head.is-stuck{
    padding-top: 12px;
    padding-bottom: 12px;
    box-shadow: 0 1px 0 var(--a-line), 0 8px 20px -12px rgba(23, 32, 43, 0.25);
}
.rd-head-row{
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}
.rd-head-title{ transition: font-size 0.2s; }
.rd-head.is-stuck .rd-head-eyebrow{ display: none; }
.rd-head.is-stuck .rd-head-title{ font-size: 1.15rem; }
.rd-head.is-stuck .rd-meta{ margin-top: 4px; }

.rd-live{
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    padding: 8px 8px 8px 12px;
    border: 1px solid #f3c9cb;
    border-radius: 10px;
    background: var(--a-surface);
    flex-wrap: wrap;
}
.rd-head.is-stuck .rd-live{ margin-top: 10px; }
.rd-live-badge{
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--a-live);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    flex-shrink: 0;
    &.is-paused{ background: #8a6a1f; }
}
.rd-live-dot{
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: rd-blink 1.4s ease-in-out infinite;
}
.is-paused .rd-live-dot{ animation: none; }
@keyframes rd-blink{ 50%{ opacity: 0.3; } }
.rd-live-what{
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    gap: 10px;
    align-items: baseline;
    white-space: nowrap;
    overflow: hidden;
}
@media (min-width: 992px){
    .rd-live{ flex-wrap: nowrap; }
}
.rd-live-progress{ color: var(--a-text-2); font-size: 0.8rem; flex-shrink: 0; }
.rd-live-title{ font-weight: 600; overflow: hidden; text-overflow: ellipsis; }
.rd-meta{
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin-top: 8px;
    font-size: 0.85rem;
}
.rd{ overflow: hidden; }

/* Stops: full-width bands between activities */
.rd-stop{
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 18px;
    background: var(--a-surface-2);
    border-bottom: 1px solid var(--a-line);
    border-left: 3px solid var(--a-accent);
}
.rd-stop-start{ border-left-color: var(--a-ink); }
/* Live row: a slow, soft heartbeat of the "on air" tint - you see where
   the play is at a glance without it shouting. */
.rd-stop.is-live{
    border-left-color: var(--a-live);
    animation: rd-heartbeat-stop 2.6s ease-in-out infinite;
}
@keyframes rd-heartbeat-stop{
    0%, 100%{ background-color: #fdecec; }
    50%{ background-color: #fbdcdd; }
}
.rd-stop-icon{ font-size: 1.05rem; color: var(--a-accent-ink); width: 20px; text-align: center; }
.rd-stop-start .rd-stop-icon{ color: var(--a-ink); }
.rd-stop-name{ font-weight: 700; }
.rd-stop-what{ font-size: 0.8rem; color: var(--a-text-2); }
.rd-stop-live{ font-size: 0.8rem; font-weight: 700; color: var(--a-live); margin-top: 2px; }

/* Activities */
.rd-item{ border-bottom: 1px solid var(--a-line); }
.rd-item:last-child{ border-bottom: 0; }
.rd-item.is-live{
    box-shadow: inset 3px 0 0 var(--a-live);
    animation: rd-heartbeat 2.6s ease-in-out infinite;
}
@keyframes rd-heartbeat{
    0%, 100%{ background-color: #fff8f8; }
    50%{ background-color: #ffeaea; }
}
.rd-item-row{
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px 10px 18px;
    cursor: pointer;
    min-height: 54px;
}
.rd-item-row:hover{ background: var(--a-surface-2); }
.rd-item.is-live .rd-item-row:hover{ background: transparent; }
.rd-num{
    width: 26px;
    text-align: right;
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    color: var(--a-text-3);
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
}
.rd-type{ width: 128px; flex-shrink: 0; }
.rd-facts{
    display: flex;
    gap: 14px;
    color: var(--a-text-2);
    font-size: 0.8rem;
    white-space: nowrap;
    i{ margin-right: 4px; color: var(--a-text-3); }
}
.rd-chevron{ color: var(--a-text-3); }

.rd-detail{ padding: 4px 18px 18px 98px; }
.rd-detail-facts{
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    font-size: 0.8rem;
    color: var(--a-text-2);
    margin-bottom: 10px;
}
.rd-choices{ display: flex; flex-direction: column; gap: 6px; max-width: 720px; }
.rd-choice{
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border: 1px solid var(--a-line);
    border-radius: 6px;
    overflow: hidden;
    background: var(--a-surface);
}
.rd-choice-bar{
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--a-surface-2);
    transition: width 0.4s ease;
}
.rd-choice.is-correct{ border-color: #9fd7b6; }
.rd-choice.is-correct .rd-choice-bar{ background: var(--a-ok-soft); }
.rd-choice > *:not(.rd-choice-bar){ position: relative; }
.rd-choice-letter{ font-weight: 800; color: var(--a-text-3); width: 14px; }
.rd-choice-text{ flex: 1; }
.rd-choice-ok{ color: var(--a-ok); }
.rd-choice-count{
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    min-width: 24px;
    text-align: right;
    cursor: help;
}
.rd-answers{ display: flex; flex-wrap: wrap; gap: 6px; }
.rd-answer{
    border: 1px solid var(--a-line);
    background: var(--a-surface);
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 0.8rem;
    &:hover{ border-color: var(--a-text-3); }
}

/* Type picker */
.type-grid{
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 10px;
}
.type-card{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    text-align: left;
    padding: 14px;
    border: 1px solid var(--a-line-strong);
    border-radius: 8px;
    background: var(--a-surface);
    &:hover:not(:disabled){ border-color: var(--a-accent); box-shadow: 0 0 0 2px var(--a-accent-soft); }
    &:disabled{ opacity: 0.45; }
    .bi{ font-size: 1.3rem; color: var(--a-accent-ink); margin-bottom: 6px; }
}
.type-card-name{ font-weight: 700; }
.type-card-desc{ font-size: 0.8rem; color: var(--a-text-2); }

/* Stop resources */
.res-row{
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px;
    margin-bottom: 8px;
    border: 1px solid var(--a-line);
    border-radius: 8px;
    background: var(--a-surface-2);
    .a-handle{ margin-top: 4px; }
}
.res-title{ font-weight: 700; font-size: 0.85rem; margin-bottom: 6px; display: flex; gap: 6px; align-items: center; }

@media (prefers-reduced-motion: reduce){
    .rd-item.is-live, .rd-stop.is-live{ animation: none; }
    .rd-item.is-live{ background-color: #fff3f3; }
    .rd-stop.is-live{ background-color: var(--a-live-soft); }
}
@media (max-width: 991.98px){
    .rd-head{ margin: -20px -16px 16px; padding: 18px 16px 12px; }
    .rd-head.is-stuck .rd-meta{ display: none; }
    .rd-facts{ display: none; }
    .rd-detail{ padding-left: 18px; }
    .rd-item-row{ gap: 8px; padding-left: 10px; }
    .rd-num{ width: 18px; }
}
/* Phones: the title is what matters - let it wrap, keep only the type icon. */
@media (max-width: 575.98px){
    .rd-type{ width: auto; font-size: 0; gap: 0; padding: 3px 6px; .bi{ font-size: 0.85rem; } }
    .rd-item-row .a-row-title{ white-space: normal; }
    .rd-item-row .a-pill-muted{ display: none; }
    .rd-item-row .a-handle{ display: none; }
}
</style>
